import { useEffect, useMemo, useState } from "react";
import { api } from "../../../api";
import type { Client, Professional } from "../../../types";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Calendar } from "../../../components/ui/calendar";
import { Input } from "../../../components/ui/input";
import { Combobox } from "../../../components/ui/combobox";
import { parseBrazilDate } from "../../utils/date";
import { SLOT_STEP } from "../utils";
import { toast } from "sonner";
import { useLanguage } from "../../../contexts/LanguageContext";
import { apptLabels } from "../labels";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet";
import { X } from "../../../components/icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

interface Props {
  apptId: number | string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function AppointmentEditDrawer({ apptId, onClose, onSuccess }: Props) {
  const { lang } = useLanguage();
  const t = apptLabels[lang];
  
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [appt, setAppt] = useState<any | null>(null);
  const [specialties, setSpecialties] = useState<Array<{ id: number; name: string }>>([]);
  const [insuranceProviders, setInsuranceProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [specialtyId, setSpecialtyId] = useState<number | null>(null);
  const [insuranceProviderId, setInsuranceProviderId] = useState<number | null>(null);
  const [date, setDate] = useState<Date>(() => new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedEndTime, setSelectedEndTime] = useState<string | null>(null);
  const [confirmScope, setConfirmScope] = useState(false);
  const [pendingBody, setPendingBody] = useState<any>(null);

  const professionalOptions = useMemo(
    () => professionals.map((prof) => ({ value: String(prof.id), label: prof.name })),
    [professionals]
  );
  const clientOptions = useMemo(
    () => clients.map((client) => ({ value: String(client.id), label: client.name })),
    [clients]
  );
  const specialtyOptions = useMemo(
    () => specialties.map((s) => ({ value: String(s.id), label: s.name })),
    [specialties]
  );
  const insuranceOptions = useMemo(
    () => insuranceProviders.map((ip) => ({ value: String(ip.id), label: ip.name })),
    [insuranceProviders]
  );

  const mysqlDateStr = useMemo(() => {
    const pad2 = (n: number) => String(n).padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
  }, [date]);

  useEffect(() => {
    (async () => {
      const [profs, cls, specs, ins] = await Promise.all([
        api.listProfessionals(),
        api.listClients(),
        api.listCompanySpecialties(),
        api.listInsuranceProviders(),
      ]);
      if (profs.ok) setProfessionals(profs.data || []);
      if (cls.ok) setClients(cls.data || []);
      if (specs.ok) setSpecialties(specs.data || []);
      if (ins.ok) setInsuranceProviders(ins.data || []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!apptId) return;
      const res = await api.getAppointment(apptId);
      if (!res.ok) return;
      const a = res.data;
      setAppt(a);
      setEmployeeId(a.employee_id ?? null);
      setSpecialtyId(a.specialty_id ?? null);
      setInsuranceProviderId(a.insurance_provider_id ?? null);
      
      const start = parseBrazilDate(a.start_datetime);
      const pad2 = (n: number) => String(n).padStart(2, '0');
      const day = `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}-${pad2(start.getUTCDate())}`;
      const time = `${pad2(start.getUTCHours())}:${pad2(start.getUTCMinutes())}`;
      const [yy, mm, dd] = day.split("-").map(Number);
      const base = new Date(Date.UTC(yy, (mm || 1) - 1, dd || 1));
      setDate(base);
      setSelectedTime(time);

      const end = a.end_datetime ? parseBrazilDate(a.end_datetime) : null;
      let eh: number, em: number;
      if (end) {
        eh = end.getUTCHours();
        em = end.getUTCMinutes();
      } else {
        const [ih, im] = time.split(":").map(Number);
        const dS = new Date(base); dS.setUTCHours(ih, im, 0, 0);
        const dE = new Date(dS); dE.setUTCMinutes(dE.getUTCMinutes() + Number(a.duration_minutes || 60));
        eh = dE.getUTCHours();
        em = dE.getUTCMinutes();
      }
      const snapped = em - (em % SLOT_STEP);
      setSelectedEndTime(`${String(eh).padStart(2,'0')}:${String(snapped).padStart(2,'0')}`);
    })();
  }, [apptId]);

  const times = useMemo(() => {
    const out: string[] = [];
    for (let m = 0; m < 24 * 60; m += SLOT_STEP) {
      const h = Math.floor(m / 60);
      const mi = m % 60;
      out.push(`${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`);
    }
    return out;
  }, []);

  const isDone = Boolean(appt?.done);
  const canSave = useMemo(() => {
    if (isDone) return !!employeeId && !!specialtyId;
    if (!selectedTime || !selectedEndTime || !employeeId || !specialtyId) return false;
    const [sh, sm] = selectedTime.split(":").map(Number);
    const [eh, em] = selectedEndTime.split(":").map(Number);
    return (eh * 60 + em) > (sh * 60 + sm);
  }, [isDone, selectedTime, selectedEndTime, employeeId, specialtyId]);

  const performSave = async (body: any, all: boolean) => {
    if (!apptId) return;
    setLoading(true);
    try {
      const res = all && appt.recurrence_group_id
        ? await api.updateRecurringAppointments(appt.recurrence_group_id, { appointment_id: Number(apptId), ...body })
        : await api.updateAppointment(Number(apptId), body);

      if (!res.ok) throw new Error(res.error || t.editPage.saveError);
      
      toast.success(t.editPage.updated);
      onSuccess();
    } catch (e: any) {
      toast.error(String(e?.message || e || t.editPage.saveError));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canSave || !apptId) return;

    const body: any = {
      employee_id: Number(employeeId || appt.employee_id),
      specialty_id: specialtyId,
      insurance_provider_id: insuranceProviderId,
    };

    if (!isDone) {
      const start_datetime = `${mysqlDateStr} ${selectedTime}:00`;
      const [eh, em] = String(selectedEndTime).split(":").map(Number);
      const [sh, sm] = String(selectedTime).split(":").map(Number);
      const dS = new Date(date); dS.setUTCHours(sh, sm, 0, 0);
      const dE = new Date(date); dE.setUTCHours(eh, em, 0, 0);
      const duration_minutes = Math.max(15, Math.round((+dE - +dS) / 60000));
      
      body.client_id = appt.client_id ?? null;
      body.start_datetime = start_datetime;
      body.duration_minutes = duration_minutes;
    }

    if (appt?.recurrence_group_id) {
      setPendingBody(body);
      setConfirmScope(true);
      return;
    }

    await performSave(body, false);
  };

  const handleMarkDone = async () => {
    if (!apptId) return;
    setLoading(true);
    const res = await api.updateAppointment(apptId, { done: true });
    setLoading(false);
    if (res.ok) {
      toast.success(t.toasts.markedDone);
      onSuccess();
    } else {
      toast.error(res.error || t.toasts.updateError);
    }
  };

  return (
    <Sheet open={!!apptId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-2xl h-full flex flex-col p-0 border-l">
        <SheetHeader className="px-6 py-4 border-b flex flex-row items-center justify-between">
          <SheetTitle className="text-xl font-bold">
            {t.editPage.title} {appt?.client_name ? `- ${appt.client_name}` : ""}
          </SheetTitle>
        </SheetHeader>

        {!appt ? (
          <div className="flex-1 flex items-center justify-center p-8 text-neutral-500">
            {t.editPage.loading}
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-white">
              {/* Sessão 1: Identificação */}
              <section className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-neutral-700 uppercase tracking-wider">{t.editModal.professional}</Label>
                    <Combobox
                      options={professionalOptions}
                      value={employeeId != null ? String(employeeId) : undefined}
                      onChange={(v) => setEmployeeId(v ? Number(v) : null)}
                      placeholder={t.editModal.select}
                      searchPlaceholder={t.newPage.comboboxSearch}
                      emptyMessage={t.newPage.comboboxEmpty}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-neutral-700 uppercase tracking-wider">{t.editModal.specialty} <span className="text-red-500">*</span></Label>
                    <Combobox
                      options={specialtyOptions}
                      value={specialtyId != null ? String(specialtyId) : undefined}
                      onChange={(v) => setSpecialtyId(v ? Number(v) : null)}
                      placeholder={t.editModal.select}
                      searchPlaceholder={t.newPage.comboboxSearch}
                      emptyMessage={t.newPage.comboboxEmpty}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-neutral-700 uppercase tracking-wider">{t.newPage.insurance}</Label>
                    <Combobox
                      options={insuranceOptions}
                      value={insuranceProviderId != null ? String(insuranceProviderId) : undefined}
                      onChange={(v) => setInsuranceProviderId(v ? Number(v) : null)}
                      placeholder={t.editModal.select}
                      searchPlaceholder={t.newPage.comboboxSearch}
                      emptyMessage={t.newPage.comboboxEmpty}
                      className="w-full"
                    />
                  </div>
                </div>
              </section>

              <section className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-neutral-700 uppercase tracking-wider">{t.editModal.date}</Label>
                  <div className="border rounded-xl bg-white shadow-sm overflow-hidden p-1">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => { if (d) setDate(d); }}
                      className="w-full"
                      classNames={{
                        cell: "h-8 w-full text-center text-sm p-0 relative",
                        day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 mx-auto",
                        head_cell: "text-neutral-500 rounded-md w-full font-normal text-[0.8rem] py-1",
                        table: "w-full border-collapse space-y-1",
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-neutral-700 uppercase tracking-wider">{t.editModal.start}</Label>
                    <Input
                      type="time"
                      value={selectedTime || ""}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="h-10 font-medium"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-neutral-700 uppercase tracking-wider">{t.editModal.end}</Label>
                    <Input
                      type="time"
                      value={selectedEndTime || ""}
                      onChange={(e) => setSelectedEndTime(e.target.value)}
                      className="h-10 font-medium"
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className="p-6 border-t bg-white flex items-center justify-end gap-3 mt-auto">
              <Button
                variant="outline"
                onClick={onClose}
                className="h-10 px-6"
              >
                {t.common.cancel}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave || loading}
                className="h-10 px-8"
              >
                {loading ? t.common.loading : t.common.save}
              </Button>
            </div>
          </>
        )}
      </SheetContent>

      <AlertDialog open={confirmScope} onOpenChange={v => { setConfirmScope(v); if(!v) setPendingBody(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle>{t.editModal.recurrence.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.editModal.recurrence.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={async () => {
                if (!pendingBody) return;
                setConfirmScope(false);
                await performSave(pendingBody, false);
                setPendingBody(null);
              }}
            >
              {t.editModal.recurrence.onlyThis}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!pendingBody) return;
                setConfirmScope(false);
                await performSave(pendingBody, true);
                setPendingBody(null);
              }}
            >
              {t.editModal.recurrence.allNext}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
